import { Injectable } from '@angular/core';
import { Observable, Subscription, map, debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs';
import { FormGroupType } from '@app/core/utils/formgroup-type';
import {
  MeteringGridAreaDto,
  MeteringPointDto,
  MeteringPointService,
  MeteringPointSubType,
  WashInstruction
} from '@app/api/dmc';
import { ConnectionPointSearchResult, ConnectionPointService } from '@app/api/nts';
import { ToastService } from '@app/core/services/toast.service';
import { ModalService } from '@app/core/services/modal.service';
import {
  FindMeteringpointModalComponent
} from '@shared/modals/meteringpoint/find-meteringpoint-modal/find-meteringpoint-modal.component';
import {
  ConnectionpointSearchComponent
} from '@shared/modals/meteringpoint/connectionpoint-search/connectionpoint-search.component';
import { KeyValue } from '@shared/interfaces/key-value/key-value';

@Injectable({ providedIn: 'root' })
export class MeteringPointFormHelperService {
  constructor(
    private readonly connectionPointService: ConnectionPointService,
    private readonly meteringPointService: MeteringPointService,
    private readonly toastService: ToastService,
    private readonly modalService: ModalService,
  ) {}

  loadGridAreas(): Observable<KeyValue[]> {
    return this.meteringPointService.meteringPointGetAllGridAreasGet().pipe(
      map((data: MeteringGridAreaDto[]) =>
        data.map((d: MeteringGridAreaDto) => ({ key: d.identification ?? '', value: d.name ?? '' }))
      )
    );
  }

  populateFromConnectionPointID(form: FormGroupType<any>, id: string, silent = false): void {
    if (!id) return;

    this.connectionPointService.connectionPointSearchPost({ connectionPointIdentification: id }, 1, 20)
      .subscribe((data) => {
        if (data.results?.length === 1) {
          this.populateAddressFormFromConnectionPoint(form, data.results[0], silent);
        }
      });
  }

  populateFromMeteringPointID(form: FormGroupType<any>, id: string): void {
    if (!id) return;

    this.meteringPointService.meteringPointGetByIdentificationGet(id).subscribe((result: MeteringPointDto) => {
      if (result) {
        this.toastService.show('meteringPoint.create.meteringPointParentAdded');
        if (result?.installationLocationAddress) {
          result.installationLocationAddress.country = 'DK';
          form.get('installationLocationAddress')?.patchValue(result.installationLocationAddress);
        }
      }
    });
  }

  registerConnectionPointChange(form: FormGroupType<any>): Subscription {
    return form.get('connectionPointIdentification')?.valueChanges.pipe(
      debounceTime(300),
      map((value: string) => value?.replaceAll(/\D+/g, "") ?? ""),
      filter((value: string) => !!value),
      distinctUntilChanged(),
      switchMap((value: string) =>
        this.connectionPointService.connectionPointSearchPost({ connectionPointIdentification: value }, 1, 20)
      )
    ).subscribe((data) => {
      if (data.results?.length === 1) {
        this.populateAddressFormFromConnectionPoint(form, data.results[0]);
      }
    }) ?? Subscription.EMPTY;
  }

  populateAddressFormFromConnectionPoint(form: FormGroupType<any>, data: ConnectionPointSearchResult, silent = false): void {
    if (data?.connectionPointIdentification && data?.address) {
      form.get('connectionPointIdentification')?.patchValue(data.connectionPointIdentification, { emitEvent: false });
      form.get('connectionPointAddress')?.patchValue(data.address);
    }

    if (data.id) {
      form.get('connectionPointId')?.patchValue(data.id);
    }

    if (data.addressDetails) {
      form.get('installationLocationAddress')?.patchValue(data.addressDetails);
      form.get('installationLocationAddress.washInstructions')?.setValue(WashInstruction.Washable);
      if (!silent) {
        this.toastService.show('meteringPoint.create.addressAdded');
      }
    } else {
      form.get('installationLocationAddress')?.setValue({});
    }
  }

  setupSubTypeHandling(form: FormGroupType<any>): Subscription {
    return form.get('detailCharacteristics.subType')?.valueChanges.subscribe((subType: MeteringPointSubType) => {
      if (subType === MeteringPointSubType.Physical) {
        form.get('detailMeterCharacteristic')?.enable();
      } else {
        form.get('detailMeterCharacteristic')?.disable();
      }
    }) ?? Subscription.EMPTY;
  }

  setupParentMeteringPointHandling(form: FormGroupType<any>, onChildStateChange: (isChild: boolean) => void): Subscription {
    return form.get('parentMeteringPointIdentification')?.valueChanges.subscribe((parentID: any) => {
      if (!parentID) return;
      parentID = parentID.replaceAll(/\D+/g, "");

      if (parentID.length === 18) {
        this.populateFromMeteringPointID(form, parentID);
        onChildStateChange(true);
        form.get('detailCharacteristics.connectionType')?.disable();
        form.get('detailCharacteristics.settlementMethod')?.disable();
      } else {
        onChildStateChange(false);
        form.get('detailCharacteristics.connectionType')?.enable();
        form.get('detailCharacteristics.settlementMethod')?.enable();
      }
    }) ?? Subscription.EMPTY;
  }

  openMeteringPointModal(): Observable<string> {
    return this.modalService.show({
      component: FindMeteringpointModalComponent,
      title: 'meteringPoint.find.modal.title',
      size: 'xl',
      actions: [
        { type: 'primary', label: 'common.select', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ]
    });
  }

  openConnectionPointSearchModal(): Observable<ConnectionPointSearchResult> {
    return this.modalService.show({
      component: ConnectionpointSearchComponent,
      title: 'meter.createform.findconnectionpoint',
      size: 'lg',
      actions: [
        { type: 'primary', label: 'system.button.attach', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
    });
  }
}
